import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import { sanitizeErrorMessage } from '@/lib/errors';
import { checkImportLimits, describeViolations } from '@/lib/workflowLimitsCheck';
import type { PlanLimits } from '@/lib/planTiers';
import type { WorkflowState, MultiSheetWorkflowState } from '@/types/workflow';

// A-015: union-signaturerna matchar faktiska return-typer från
// useDocuments + useMultiSheetWorkflow — ersätter `as unknown as`-casts
// i Index.tsx. `deleteDocument` returnerar egentligen Promise<boolean>
// (useDocuments:451), inte Promise<void> som tidigare deklarerats.
export type LoadableWorkflowState = WorkflowState | MultiSheetWorkflowState;

export interface UseLoadHandlersDeps {
  loadState: (state: LoadableWorkflowState) => void;
  resetLayerPath: () => void;
  setCurrentDocumentId: (id: string | null) => void;
  currentDocumentId: string | null;
  loadDocument: (id: string) => Promise<LoadableWorkflowState | null>;
  deleteDocument: (id: string) => Promise<boolean>;
  /** Plan-limits för att gate over-limit-laddningar till /preview. */
  limits: PlanLimits;
  logAction: (action: string, detail?: string) => void;
  t: (key: string) => string;
  tCount: (key: string, count: number) => string;
}

export function useLoadHandlers(deps: UseLoadHandlersDeps) {
  const {
    loadState, resetLayerPath, setCurrentDocumentId, currentDocumentId,
    loadDocument, deleteDocument, limits, logAction, t, tCount,
  } = deps;
  const navigate = useNavigate();

  /**
   * Free-tier (eller downgraded) users kan ha workflow:er sparade när de
   * var paid — eller försöka ladda en .json fil de fått från någon annan.
   * Båda fallen routas till /preview om state överstiger plan-gränsen,
   * exakt samma read-only sandbox som ImportJsonDialog använder.
   */
  const checkAndRouteIfOverLimit = useCallback((state: LoadableWorkflowState): boolean => {
    const report = checkImportLimits(state, limits);
    if (report.withinLimits) return false;
    navigate('/flow-weaver/preview', { state: { workflow: state } });
    toast({
      title: 'Workflow överstiger plan-gräns',
      description: `${describeViolations(report.violations)}. Visar i read-only preview — uppgradera för att redigera.`,
      variant: 'destructive',
    });
    return true;
  }, [limits, navigate]);

  /** Upload a .json file from disk and import it. */
  const handleLoad = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const parsed = JSON.parse(text);
        const { isCompactFormat, expandCompactWorkflow } = await import('@/lib/compactWorkflow');
        let data = parsed;
        if (isCompactFormat(data)) data = expandCompactWorkflow(data);
        const { parseWorkflowState } = await import('@/lib/workflowSchema');
        const state = parseWorkflowState(data);
        if (state) {
          const stateForLoad = state as Parameters<typeof loadState>[0];
          if (checkAndRouteIfOverLimit(stateForLoad)) {
            logAction('Loaded (preview-route)', `From file: ${file.name}`);
            return;
          }
          // Zod-default ('') gör name garanterat string, men inferred-type
          // visar ibland optional. Cast via Loadable så loadState accepterar.
          loadState(stateForLoad);
          setCurrentDocumentId(null);
          resetLayerPath();
          logAction('Loaded', `From file: ${file.name}`);
          toast({
            title: t('loaded'),
            description: `${file.name} — ${tCount('blocksCount', state.blocks.length)}`,
          });
        } else {
          toast({ title: t('invalidJson'), description: t('invalidStructure'), variant: 'destructive' });
        }
      } catch {
        toast({ title: t('invalidJson'), description: t('couldNotReadWorkflow'), variant: 'destructive' });
      }
    };
    input.click();
  }, [loadState, setCurrentDocumentId, resetLayerPath, checkAndRouteIfOverLimit, logAction, t, tCount]);

  /** Load a workflow from Supabase by id. */
  const handleLoadFromDb = useCallback(async (id: string, title: string) => {
    try {
      const state = await loadDocument(id);
      if (state) {
        if (checkAndRouteIfOverLimit(state)) return;
        loadState(state);
        setCurrentDocumentId(id);
        resetLayerPath();
        toast({ title: t('loaded'), description: `"${title}" ${t('loaded').toLowerCase()}` });
      } else {
        toast({ title: t('error'), description: t('couldNotReadWorkflow'), variant: 'destructive' });
      }
    } catch (err: unknown) {
      toast({ title: t('loadError'), description: sanitizeErrorMessage(err), variant: 'destructive' });
    }
  }, [loadDocument, loadState, setCurrentDocumentId, resetLayerPath, checkAndRouteIfOverLimit, t]);

  const handleDeleteFromDb = useCallback(async (id: string) => {
    try {
      await deleteDocument(id);
      if (currentDocumentId === id) setCurrentDocumentId(null);
      toast({ title: t('deleted'), description: t('documentDeleted') });
    } catch (err: unknown) {
      toast({ title: t('deleteError'), description: sanitizeErrorMessage(err), variant: 'destructive' });
    }
  }, [deleteDocument, currentDocumentId, setCurrentDocumentId, t]);

  return { handleLoad, handleLoadFromDb, handleDeleteFromDb, checkAndRouteIfOverLimit };
}
